import { Printer } from 'lucide-react';
import React from 'react';
import { useTranslation } from 'react-i18next';
import GuidebookPageBody, { GuidebookPage } from '../components/guidebook/GuidebookPageBody';
import ProtectedRoute from '../components/ProtectedRoute';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { useReservation } from '../contexts/ReservationContext';
import { apiClient, guidebooksApi } from '../lib/api';

/**
 * BES-8422: printable guidebook for one listing.
 *
 * Opened from the dashboard's Guide List "Print" button at
 *   /print/guidebook#token=<jwt>
 * The token is a host-minted, 10 minute listing preview token carrying `print: true`
 * (see POST /api/guidebooks/print-token). It rides in the URL fragment so it never
 * reaches the server or a Referer header, and is removed from the URL as soon as it is
 * read so the browser's print header shows a clean address.
 *
 * Every guidebook page renders stacked, in section order then page order, using the
 * same GuidebookPageBody as the on-screen view. styles/guidebook.css puts each page on
 * its own sheet and lets long pages flow onto the next. The browser's own print dialog
 * handles paper size, "Save as PDF", and the printer.
 *
 * Browser header/footer (date, tab title, URL, page number): browsers draw these inside
 * the @page margin and skip them when it is 0, so the stylesheet zeroes the margin and
 * insets the content instead, using <thead>/<tfoot> rows that repeat on every sheet.
 * WebKit never repeats those rows when printing (WebKit bug 17205), so on Safari the
 * later sheets would print flush to the paper edge. Safari therefore keeps a real 0.5in
 * @page margin, and with it the browser header/footer, which the host can untick in the
 * print dialog.
 */

/** Safari and every other WebKit browser (all iOS browsers included). Chrome and Firefox
 *  report a different vendor. */
const isWebKit = typeof navigator !== 'undefined' && navigator.vendor === 'Apple Computer, Inc.';

interface PrintSection {
  id: string;
  name: string;
  pages: GuidebookPage[];
}

function readTokenFromFragment(): string | null {
  const raw = window.location.hash.startsWith('#')
    ? window.location.hash.slice(1)
    : window.location.hash;
  if (!raw) return null;
  return new URLSearchParams(raw).get('token');
}

/** Resolve once every image on the page has decoded, or after `timeoutMs`, whichever is first. */
async function waitForImages(timeoutMs: number): Promise<void> {
  const decodes = Array.from(document.images).map((img) => img.decode().catch(() => undefined));
  await Promise.race([
    Promise.all(decodes),
    new Promise<void>((resolve) => setTimeout(resolve, timeoutMs)),
  ]);
}

type TokenState = 'pending' | 'ok' | 'missing';

/** How long the auto-print waits for the listing name before printing without it. */
const TITLE_WAIT_MS = 3000;

export default function GuidebookPrint() {
  const { t } = useTranslation('portal');
  const { login, isAuthenticated } = useAuth();
  const { language } = useLanguage();
  const { summary, loaded: summaryLoaded } = useReservation();

  // Read the fragment exactly once, before any effect can scrub it. (An effect that
  // re-runs, e.g. under React StrictMode, would otherwise see an empty hash.)
  const [fragmentToken] = React.useState<string | null>(readTokenFromFragment);
  const [tokenState, setTokenState] = React.useState<TokenState>('pending');
  const [sections, setSections] = React.useState<PrintSection[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  const [titleReady, setTitleReady] = React.useState(false);
  const autoPrinted = React.useRef(false);

  // 1. Log in with the fragment token and scrub it from the URL.
  React.useEffect(() => {
    if (tokenState !== 'pending') return;
    if (fragmentToken) {
      apiClient.setToken(fragmentToken);
      login(fragmentToken);
      if (window.location.hash) window.history.replaceState(null, '', window.location.pathname);
      setTokenState('ok');
      return;
    }
    // No fragment, e.g. a reload after the URL was scrubbed. The session AuthContext
    // restored from sessionStorage is enough; ProtectedRoute still checks scope and expiry.
    if (isAuthenticated) {
      setTokenState('ok');
      return;
    }
    setTokenState('missing');
    setLoading(false);
  }, [fragmentToken, isAuthenticated, login, tokenState]);

  // 2. Fetch every page for the listing once the token is accepted.
  React.useEffect(() => {
    if (tokenState !== 'ok' || !isAuthenticated) return;
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        const ui = await guidebooksApi.getUi(language);
        if (cancelled) return;
        setSections((ui.sections || []) as unknown as PrintSection[]);
      } catch (e: any) {
        if (cancelled) return;
        setError(e?.message || t('Failed to load guidebook'));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [tokenState, isAuthenticated, language, t]);

  // 3. The tab title is what "Save as PDF" uses as the default filename.
  const title = summary?.listingName
    ? t('{{listingName}} Guidebook', { listingName: summary.listingName })
    : t('Guidebook');
  React.useEffect(() => {
    if (summary?.listingName) document.title = title;
  }, [summary?.listingName, title]);

  // The reservation summary (listing name) loads independently of the pages. Hold the
  // auto-print until it has landed, or for TITLE_WAIT_MS, so "Save as PDF" gets the
  // listing name as its default filename rather than the app title.
  React.useEffect(() => {
    if (summaryLoaded) {
      setTitleReady(true);
      return;
    }
    const timer = setTimeout(() => setTitleReady(true), TITLE_WAIT_MS);
    return () => clearTimeout(timer);
  }, [summaryLoaded]);

  const pages = React.useMemo(() => sections.flatMap((s) => s.pages), [sections]);

  const print = React.useCallback(async () => {
    await waitForImages(15_000);
    window.print();
  }, []);

  // 4. Open the print dialog once, after the pages, their images, and the title are ready.
  React.useEffect(() => {
    if (loading || error || pages.length === 0 || !titleReady || autoPrinted.current) return;
    autoPrinted.current = true;
    let cancelled = false;
    (async () => {
      // Let React paint the pages before we start waiting on their images.
      await new Promise((resolve) => requestAnimationFrame(resolve));
      if (!cancelled) await print();
    })();
    return () => {
      cancelled = true;
    };
  }, [loading, error, pages.length, titleReady, print]);

  if (tokenState === 'pending') return null;

  return (
    <ProtectedRoute requireScope="guidebooks">
      <div className="min-h-screen bg-white">
        {/* @page cannot be scoped by a selector, so the Safari override is a style
            element rendered after the stylesheet. See the module comment. */}
        {isWebKit && <style>{'@media print { @page { margin: 0.5in; } }'}</style>}
        <div
          className={`print-root max-w-[720px] mx-auto px-4 py-6${isWebKit ? ' print-root-webkit' : ''}`}
        >
          {/* Screen-only toolbar. Hidden in print by .no-print. */}
          <div className="no-print sticky top-0 z-20 -mx-4 px-4 py-3 mb-8 bg-white/95 backdrop-blur-sm border-b border-gray-200 flex items-center justify-between gap-4">
            <div className="min-w-0">
              <div className="text-base font-semibold text-gray-900 truncate">{title}</div>
              <div className="text-sm text-gray-500">
                {loading
                  ? t('Loading pages...')
                  : pages.length === 1
                    ? t('{{count}} page', { count: 1 })
                    : t('{{count}} pages', { count: pages.length })}
              </div>
              {isWebKit && (
                <div className="text-xs text-gray-500 mt-1">
                  {t(
                    'Safari adds the date, title and address to each printed page. To leave them off, untick "Print headers and footers" in the print dialog.'
                  )}
                </div>
              )}
            </div>
            <button
              type="button"
              onClick={() => void print()}
              disabled={loading || pages.length === 0}
              className="inline-flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50 flex-shrink-0"
            >
              <Printer className="w-4 h-4" />
              {t('Print')}
            </button>
          </div>

          {error && (
            <div className="no-print rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-700">
              {error}
            </div>
          )}

          {!loading && !error && pages.length === 0 && (
            <div className="no-print rounded-lg border border-gray-200 bg-gray-50 p-4 text-sm text-gray-600">
              {t('This listing has no guidebook pages.')}
            </div>
          )}

          {/* The table exists for print only: its header and footer rows repeat on every
              sheet and stand in for the @page margin (see guidebook.css). On screen both
              rows are hidden and the table is a plain block wrapper. */}
          <table className="print-sheet w-full">
            <thead>
              <tr>
                <td className="print-sheet-margin" aria-hidden="true" />
              </tr>
            </thead>
            <tbody>
              <tr>
                <td className="p-0 align-top">
                  {pages.map((page) => (
                    <article key={page.id} className="print-page mb-12 last:mb-0">
                      <h1 className="text-lg font-semibold text-gray-900 leading-tight mb-6">
                        {page.title}
                      </h1>
                      <GuidebookPageBody page={page} showMap={false} />
                    </article>
                  ))}
                </td>
              </tr>
            </tbody>
            <tfoot>
              <tr>
                <td className="print-sheet-margin" aria-hidden="true" />
              </tr>
            </tfoot>
          </table>
        </div>
      </div>
    </ProtectedRoute>
  );
}
